/**
 * Layer 0 — wall gap / free-space computation (free zones, aligned gaps, loft-aware occupancy).
 *
 * Part of the dorm-optimizer-v2 placement engine. Split out of the former
 * monolithic placement.js (move-only refactor — bodies verbatim).
 */

const { getWallAxes } = require('../../kitchen-assembly/wallMath');
const { WALL_HALF_THICKNESS, isLoftBed } = require('../constants');

/**
 * Find ALL positions on a wall where `neededLength` fits, trying both
 * start-aligned and end-aligned within each free zone.
 * @returns {Array<{ start: number, end: number }>}
 */
// Cursor-sweep: given occupied intervals [{start,end}] over [0, totalLen],
// return the free zones [{start,end}]. Pure; shared by findAllGaps (wall runs)
// and findEdgeGaps (furniture-edge runs) so the sweep lives in one place.
function freeZonesFromOccupied(occupied, totalLen) {
  const sorted = [...occupied].sort((a, b) => a.start - b.start);
  const freeZones = [];
  let cursor = 0;
  for (const seg of sorted) {
    if (seg.start > cursor) freeZones.push({ start: cursor, end: seg.start });
    cursor = Math.max(cursor, seg.end);
  }
  if (totalLen > cursor) freeZones.push({ start: cursor, end: totalLen });
  return freeZones;
}

// From free zones, emit start-aligned, end-aligned, and center-aligned
// placements of length `neededLength`, deduped. `marginFor(zone)` returns
// `{ sMargin, eMargin }` for that zone (walls use WALL_HALF_THICKNESS at the
// physical wall ends, MARGIN elsewhere; edges use MARGIN both ends). Returns
// [{start, end}] intervals along the axis.
//
// Each gap also carries `slideMin` / `slideMax`: the lowest and highest `start`
// the item could legally take WITHIN the zone this gap came from. The three
// emitted offsets are item-width windows (`end === start + neededLength`), so
// they carry no room to move on their own; a caller repairing a rejected
// candidate needs the owning zone's bounds to know how far it may shift. Purely
// additive — every existing consumer reads only `start` / `end`.
function alignedGapsInZones(freeZones, neededLength, marginFor) {
  const gaps = [];
  const seen = new Set();
  for (const zone of freeZones) {
    const zoneLen = zone.end - zone.start;
    if (zoneLen < neededLength) continue;

    const { sMargin, eMargin } = marginFor(zone);

    const slideMin = zone.start + sMargin;
    const slideMax = zone.end - eMargin - neededLength;

    const startAligned = {
      start: zone.start + sMargin,
      end: zone.start + sMargin + neededLength,
      slideMin,
      slideMax,
    };
    const endAligned = {
      start: zone.end - eMargin - neededLength,
      end: zone.end - eMargin,
      slideMin,
      slideMax,
    };

    if (startAligned.end <= zone.end + 0.001) {
      const sKey = startAligned.start.toFixed(4);
      if (!seen.has(sKey)) {
        gaps.push(startAligned);
        seen.add(sKey);
      }
    }
    if (endAligned.start >= zone.start - 0.001) {
      const eKey = endAligned.start.toFixed(4);
      if (!seen.has(eKey)) {
        gaps.push(endAligned);
        seen.add(eKey);
      }
    }

    const centerStart = (zone.start + zone.end) / 2 - neededLength / 2;
    if (
      centerStart > zone.start + sMargin + 0.01 &&
      centerStart + neededLength < zone.end - eMargin - 0.01
    ) {
      const cKey = centerStart.toFixed(4);
      if (!seen.has(cKey)) {
        gaps.push({
          start: centerStart,
          end: centerStart + neededLength,
          slideMin,
          slideMax,
        });
        seen.add(cKey);
      }
    }
  }

  return gaps;
}

function findAllGaps(
  wallIdx,
  neededLength,
  occupiedSegments,
  roomData,
  options = {}
) {
  const wall = roomData.walls[wallIdx];
  const wallLen = wall.length;

  const occupied = [];
  if (!options.skipDoorBlockedRange) {
    for (const door of roomData.doors) {
      if (door.wallIdx === wallIdx && door.blockedRange) {
        occupied.push(door.blockedRange);
      }
    }
  }
  if (occupiedSegments[wallIdx]) {
    occupied.push(...occupiedSegments[wallIdx]);
  }

  const freeZones = freeZonesFromOccupied(occupied, wallLen);

  // Use a small margin so items don't share edges with adjacent items / arcs,
  // but hug the physical wall ends by WALL_HALF_THICKNESS.
  const MARGIN = 0.03;
  return alignedGapsInZones(freeZones, neededLength, (zone) => ({
    sMargin: zone.start < 0.01 ? WALL_HALF_THICKNESS : MARGIN,
    eMargin: zone.end > wallLen - 0.01 ? WALL_HALF_THICKNESS : MARGIN,
  }));
}

/**
 * Compute total free (unoccupied) length on a wall.
 */
function computeFreeSpace(wallIdx, occupiedSegments, roomData) {
  const wall = roomData.walls[wallIdx];

  const occupied = [];
  for (const door of roomData.doors) {
    if (door.wallIdx === wallIdx && door.blockedRange) {
      occupied.push(door.blockedRange);
    }
  }
  if (occupiedSegments[wallIdx]) {
    occupied.push(...occupiedSegments[wallIdx]);
  }

  // Merge overlapping/adjacent segments and compute total used length
  occupied.sort((a, b) => a.start - b.start);
  let used = 0;
  let end = 0;
  for (const seg of occupied) {
    const s = Math.max(seg.start, end);
    const e = seg.end;
    if (e > s) used += e - s;
    end = Math.max(end, e);
  }

  return wall.length - used;
}

// ─── Wall placement ─────────────────────────────────────────────────

/**
 * Subtract loft-bed footprints from occupied wall segments so items that tuck
 * under a loft bed can reclaim the wall span the bed occupies.
 */
function buildLoftAwareOccSegments(occupiedSegments, placedItems, roomData) {
  const loftRanges = {};
  for (const item of placedItems) {
    if (item.typeKey !== 'dorm-bed' || !isLoftBed(item.bedType)) continue;
    if (item.wallIdx == null || !item.physicalCorners) continue;
    const wall = roomData.walls[item.wallIdx];
    const axes = getWallAxes(wall);
    if (!axes) continue;
    const { wallDir } = axes;
    let minP = Infinity,
      maxP = -Infinity;
    for (const c of item.physicalCorners) {
      const p =
        (c.x - wall.start.x) * wallDir.x + (c.z - wall.start.z) * wallDir.z;
      if (p < minP) minP = p;
      if (p > maxP) maxP = p;
    }
    if (!loftRanges[item.wallIdx]) loftRanges[item.wallIdx] = [];
    loftRanges[item.wallIdx].push({
      start: Math.max(0, minP),
      end: Math.min(wall.length, maxP),
    });
  }

  if (Object.keys(loftRanges).length === 0) return occupiedSegments;

  const result = {};
  for (const k of Object.keys(occupiedSegments)) {
    const wIdx = parseInt(k, 10);
    const removals = loftRanges[wIdx];
    if (!removals || removals.length === 0) {
      result[k] = [...occupiedSegments[k]];
      continue;
    }
    let segs = [...occupiedSegments[k]];
    for (const rem of removals) {
      const next = [];
      for (const seg of segs) {
        if (seg.end <= rem.start || seg.start >= rem.end) {
          next.push(seg);
        } else {
          if (seg.start < rem.start) {
            next.push({ start: seg.start, end: rem.start });
          }
          if (seg.end > rem.end) {
            next.push({ start: rem.end, end: seg.end });
          }
        }
      }
      segs = next;
    }
    result[k] = segs;
  }
  return result;
}

module.exports = {
  alignedGapsInZones,
  buildLoftAwareOccSegments,
  computeFreeSpace,
  findAllGaps,
  freeZonesFromOccupied,
};
